import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { textStyle } from "../../styles/Style";
import "../../styles/popupStyle.css";
import InfoIcon from "@mui/icons-material/Info";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import AlertComponent from "../../subcomponents/AlertComponent";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { fieldValidationHelperText } from "../../../constants/validationHelperText";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  RESERVE_OUTLET_FIELD_ACTIVE,
  RESERVE_OUTLET_FIELD_AUTO_PRINT_RECEIPTS,
  RESERVE_OUTLET_FIELD_OUTLET_NAME,
  RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID,
  RESERVE_OUTLET_FIELD_TERMINAL_ID,
} from "../../../constants/ReserveOutletSetupFields";
import {
  reserveListAvailableHeadingTooltip,
  reserveOutletSetupTooltip,
} from "../../../constants/ReserveTooltipInformation";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const ReserveOutletSetup = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [outletName, setOutletName] = useState<string | null>(null);
  const [terminalId, setTerminalId] = useState<string | null>(null);
  const [profitCenterId, setProfitCenterId] = useState<string | null>(null);
  const [autoPrintReceipts, setAutoPrintReceipts] = useState<boolean>(true);
  const [active, setActive] = useState<boolean>(true);
  const [nameError, setNameError] = useState<boolean>(false);
  const [terminalErr, setTerminalErr] = useState<boolean>(false);
  const [profitCenterErr, setProfitCenterErr] = useState<boolean>(false);
  const [reserveOutlets, setReserveOutlets] = useState<any[]>([]);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    [RESERVE_OUTLET_FIELD_OUTLET_NAME]: outletName,
    [RESERVE_OUTLET_FIELD_TERMINAL_ID]: terminalId,
    [RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID]: profitCenterId ?? "",
    [RESERVE_OUTLET_FIELD_AUTO_PRINT_RECEIPTS]: autoPrintReceipts
      ? "Yes"
      : "No",
    [RESERVE_OUTLET_FIELD_ACTIVE]: active ? "Yes" : "No",
  };

  const validateInput = () => {
    setNameError(false);
    setTerminalErr(false);
    setProfitCenterErr(false);
    if (!outletName || outletName === "") {
      setAlertMessage("Outlet Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    if (
      outletName &&
      !validateString(
        outletName,
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setNameError(true);
      return false;
    }
    if (terminalId && terminalId !== "" && !/^\d+$/.test(terminalId)) {
      setTerminalErr(true);
      return false;
    }
    if (
      profitCenterId &&
      profitCenterId !== "" &&
      !/^\d+$/.test(profitCenterId)
    ) {
      setProfitCenterErr(true);
      return false;
    }
    return true;
  };

  const editReserveOutlets = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setOutletName(null);
      setTerminalId(null);
      setProfitCenterId(null);
      setAutoPrintReceipts(true);
      setActive(true);
      return;
    }
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setOutletName(list?.[RESERVE_OUTLET_FIELD_OUTLET_NAME] ?? null);
    setTerminalId(
      list?.[RESERVE_OUTLET_FIELD_TERMINAL_ID] != null
        ? String(list[RESERVE_OUTLET_FIELD_TERMINAL_ID])
        : null,
    );
    setProfitCenterId(
      list?.[RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID] != null &&
        list[RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID] !== ""
        ? String(list[RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID])
        : null,
    );
    setAutoPrintReceipts(
      list?.[RESERVE_OUTLET_FIELD_AUTO_PRINT_RECEIPTS] === "Yes",
    );
    setActive(list?.[RESERVE_OUTLET_FIELD_ACTIVE] === "Yes");
  };

  const updateReserveOutlets = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(
        (reserveOutlets ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.OUTLETNAME,
        outletName,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setNameError(false);
    setTerminalErr(false);
    setProfitCenterErr(false);
    setReserveOutlets((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editReserveOutlets(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.outletSetup} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addReserveOutlets = () => {
    if (!validateInput()) return;
    if (
      checkDuplicate(reserveOutlets ?? [], DNDFieldsEnum.OUTLETNAME, outletName)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setReserveOutlets((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editReserveOutlets(null);
    setEditClicked(false);
  };

  const deleteReserveOutlets = (id: number) => {
    setReserveOutlets(deleteItem(reserveOutlets ?? [], id));
  };

  useEffect(() => {
    setReserveOutlets(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(reserveOutlets);
    if (editClicked) {
      setSelectedId(checkData(reserveOutlets, selectedId) ? selectedId : null);
    }
  }, [reserveOutlets]);

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              {ReservePropertyEnum.outletSetup}
              <Tooltip
                title={tooltipComponent(reserveOutletSetupTooltip.header)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton click={() => editReserveOutlets(null)} />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editReserveOutlets(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>

          <Stack>
            <FormLabel component="legend">
              {RESERVE_OUTLET_FIELD_OUTLET_NAME}
              <Tooltip
                title={tooltipComponent(reserveOutletSetupTooltip.outletName)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveOutletName"
              variant="outlined"
              placeholder={RESERVE_OUTLET_FIELD_OUTLET_NAME}
              value={outletName ?? ""}
              helperText={
                nameError
                  ? validationMessages.outletName
                  : fieldValidationHelperText.outletName
              }
              error={nameError}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setOutletName(e.target.value || null);
                setNameError(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              {RESERVE_OUTLET_FIELD_TERMINAL_ID}
              <Tooltip
                title={tooltipComponent(reserveOutletSetupTooltip.terminalId)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveTerminalId"
              variant="outlined"
              placeholder="1"
              value={terminalId ?? ""}
              helperText={terminalErr ? "Enter numbers only" : ""}
              error={terminalErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setTerminalId(v || null);
                setTerminalErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              {RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID}
              <Tooltip
                title={tooltipComponent(
                  reserveOutletSetupTooltip.profitCenterId,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="reserveProfitCenterId"
              variant="outlined"
              placeholder={RESERVE_OUTLET_FIELD_PROFIT_CENTER_ID}
              value={profitCenterId ?? ""}
              helperText={profitCenterErr ? "Enter numbers only" : ""}
              error={profitCenterErr}
              fullWidth
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const v = e.target.value.replace(/\D/g, "");
                setProfitCenterId(v || null);
                setProfitCenterErr(false);
              }}
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Auto Print Receipts?
              <Tooltip
                title={tooltipComponent(
                  reserveOutletSetupTooltip.autoPrintReceipts,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch
                  checked={autoPrintReceipts}
                  onChange={() => setAutoPrintReceipts(!autoPrintReceipts)}
                />
              }
              label=""
            />
          </Stack>

          <Stack>
            <FormLabel component="legend">
              Active?
              <Tooltip
                title={tooltipComponent(reserveOutletSetupTooltip.active)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateReserveOutlets : addReserveOutlets}
              id="addReserveOutlet"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Outlet
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography
              variant="h6"
              component="div"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              {ReservePropertyEnum.outletSetup} available
              <Tooltip
                title={tooltipComponent(reserveListAvailableHeadingTooltip)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {Array.isArray(reserveOutlets) && reserveOutlets.length > 0 ? (
                <DraggableItemList
                  itemData={reserveOutlets}
                  onOrderUpdate={(order) => setReserveOutlets(order)}
                  onEdit={(info) => {
                    editReserveOutlets(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteReserveOutlets(id)}
                  code={DNDFieldsEnum.OUTLETNAME}
                  name={DNDFieldsEnum.TERMINALID}
                />
              ) : (
                <Typography variant="body2">
                  No {ReservePropertyEnum.outletSetup} information available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveOutletSetup;
